import React, { useCallback } from 'react';
import { useDropzone, type Accept } from 'react-dropzone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudArrowUp } from '@fortawesome/free-solid-svg-icons';
import './DropZone.scss';

const ACCEPT_MAP: Record<string, Accept> = {
  fire_file: {
    'application/pdf': ['.pdf'],
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
    'image/gif': ['.gif'],
    'application/zip': ['.zip'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'text/plain': ['.txt'],
  },
  weather_file: {
    'text/html': ['.htm', '.html'],
    'image/png': ['.png'],
    'image/jpeg': ['.jpg', '.jpeg'],
    'application/pdf': ['.pdf'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  },
  var_photo: {
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
    'image/gif': ['.gif'],
    'image/webp': ['.webp'],
  },
};

const MAX_SIZE = 50 * 1024 * 1024; // 50 MB — matches cmsUploadService

interface DropZoneProps {
  contentType: 'fire_file' | 'var_photo' | 'weather_file';
  onFilesAccepted: (files: File[]) => void;
  disabled?: boolean;
}

const DropZone: React.FC<DropZoneProps> = ({ contentType, onFilesAccepted, disabled = false }) => {
  const accept = ACCEPT_MAP[contentType] ?? {};

  const onDrop = useCallback(
    (accepted: File[]) => {
      if (accepted.length > 0) {
        onFilesAccepted(accepted);
      }
    },
    [onFilesAccepted],
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept,
    maxSize: MAX_SIZE,
    disabled,
    multiple: true,
  });

  const extensions = Object.values(accept).flat().join(', ');

  return (
    <div
      {...getRootProps()}
      className={[
        'dropzone',
        isDragActive && !isDragReject && 'dropzone--active',
        isDragReject && 'dropzone--reject',
        disabled && 'dropzone--disabled',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <input {...getInputProps()} />
      <FontAwesomeIcon icon={faCloudArrowUp} className="dropzone__icon" />
      {isDragActive && !isDragReject && <p className="dropzone__text">Drop files here</p>}
      {isDragReject && <p className="dropzone__text dropzone__text--reject">Some files are not allowed</p>}
      {!isDragActive && (
        <>
          <p className="dropzone__text">Drag and drop files here, or press to select files</p>
          <p className="dropzone__hint">{extensions} — max 50 MB per file</p>
        </>
      )}
    </div>
  );
};

export default DropZone;
